import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { updateProjectMemberAction } from "../../../actions/projects";
import { button, icon, label, number, quote } from "../../../config";
import { getTimeBlocks } from "../../Admin/ManageOrganization/OrganizationFeatures/OrganizationFeaturesContent/TimeBlocks/timeBlocks.service";
import { userRolesCollaborators, userRolesOwners } from "../../Projects/project.constants";
import { useEngagementContext } from "../Engagement.context";
import { validateInputsHelper } from "../engagement.helper";
import { updateProjectEngagement } from "../engagement.service";
import EditSidebarBlockAssignment from "./EditSidebarBlockAssignment";
import "./engagementView.scss";

/**
 * Resolve the entity id for role change based on the admin user engagement flag.
 */
function resolveRoleChangeEntityId(adminUserEngagement, project, isTeamMember, projectIdFromProps) {
  if (adminUserEngagement) {
    return project?.ProjectId;
  }
  return isTeamMember ? project?.UserId : project?.ProjectId ?? projectIdFromProps;
}

const timeBlocksCatalogCacheByCompanyKey = {};

const syncArraysEquivalent = (prev, next) => {
  if (!Array.isArray(prev) || !Array.isArray(next) || prev.length !== next.length) return false;
  return prev.every((v, i) => String(v ?? "").trim() === String(next[i] ?? "").trim());
};

/** Stable empty list for `useMemo` branches — avoids a fresh `[]` each run invalidating memoized children. */
const EMPTY_BLOCKS_LIST = [];

/**
 * Sidebar component for editing project details, including time block allocation and project allocation
 * @parent component: EngagementCard
 * @author Bhavana
 */
const EditProjectSidebar = ({
  project,
  onSave,
  onCancel,
  projectData,
  setProjectData,
  onProjectAllocationOptimistic,
  isTeamMember,
  onRoleChange,
  userRole,
  handleDeleteAction,
  setUserRole,
  ProjectId,
  checkUserValidation,
  disableClickOutside = false,
  isAdminEdit = false, 
  adminUserEngagement = false,
  closeAfterBlockSave = false,
}) => {
  const { availableTimeBlocks } = useEngagementContext();
  const [allocation, setAllocation] = useState([]);
  const [workCategory, setWorkCategory] = useState([]);
  const [workCategoryIds, setWorkCategoryIds] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const [searchValue, setSearchValue] = useState("");
  const [editedAllocation, setEditedAllocation] = useState(project.ProjectAllocation);
  const [isSaveDisabled, setIsSaveDisabled] = useState(false);
  const [availableTimeBlocksProject, setAvailableTimeBlocksProject] = useState(availableTimeBlocks);
  const [showActionRestricted, setShowActionRestricted] = useState(false);

  // Track initial values using useRef (no re-renders needed)
  const initialValues = useRef({
    allocation: [],
    workCategory: [],
    workCategoryIds: []
  });


  const RoleId = useSelector((state) => state.projectSidebar?.projectDetails?.RoleId);
  const userId = useSelector((state) => state.auth?.user?.id);
  const companyId = useSelector((state) => state.auth?.user?.companyId);
  const dispatch = useDispatch();
  const sidebarRef = useRef(null);
  const projectSnapshotRef = useRef(project);
  projectSnapshotRef.current = project;
  const projectDataRef = useRef(projectData);
  projectDataRef.current = projectData;
  const projectAllocationPercentInputRef = useRef(null);
  const allocationCommitTimeoutRef = useRef(null);
  const isAllocationInputFocusedRef = useRef(false);
  const allocationDirtyRef = useRef(false);
  const lastCommittedAllocationRef = useRef(project.ProjectAllocation ?? number.ZERO);
  const projectIdentityKey = `${project?.ProjectId ?? ""}:${project?.UserId ?? ""}`;

  // Helper function to compare arrays efficiently
  const arraysEqual = (arr1, arr2) => {
    if (arr1.length !== arr2.length) return false;
    return arr1.every((val, idx) => val === arr2[idx]);
  };


  useEffect(() => {
    if (disableClickOutside || adminUserEngagement) return;

    const handleDocumentMouseDown = (event) => {
      if (!sidebarRef.current) return;

      const clickedInsideSidebar = sidebarRef.current.contains(event.target);
      const clickedInKendoPopup = !!event.target.closest(
        ".k-animation-container, .k-popup, .k-list-container, .k-list, .k-list-item, .k-picker, .k-dialog, .k-window",
      );

      if (!clickedInsideSidebar && !clickedInKendoPopup) {
        onCancel && onCancel();
      }
    };

    document.addEventListener("mousedown", handleDocumentMouseDown);
    return () => document.removeEventListener("mousedown", handleDocumentMouseDown);
  }, [onCancel, disableClickOutside, adminUserEngagement]);

  /**
   * Handle click outside dropdown to close it
   * @author Bhavana
   */
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  /**
   * Hydrate block catalog for the company. Uses a module cache so remounts (e.g. after context updates)
   * do not call `getTimeBlocks` again. Allocation / team role changes do not change effect deps here.
   */
  useEffect(() => {
    const blockUiNeedsTimeBlockCatalog = !adminUserEngagement || showDropdown || (Array.isArray(workCategory) && workCategory.length > number.ZERO);
    if (!blockUiNeedsTimeBlockCatalog || !companyId) return;
    const companyKey = String(companyId);
    const cached = timeBlocksCatalogCacheByCompanyKey[companyKey];
    if (cached) {
      setAvailableTimeBlocksProject((prev) => (prev === cached ? prev : cached));
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const getTimeBlocksResponse = await getTimeBlocks(companyId);
        if (cancelled || !getTimeBlocksResponse) return;
        timeBlocksCatalogCacheByCompanyKey[companyKey] = getTimeBlocksResponse;
        setAvailableTimeBlocksProject(getTimeBlocksResponse);
      } catch {
        console.error("Error fetching time blocks");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [adminUserEngagement, companyId, showDropdown, workCategory?.length]);

  /** Reset “last persisted allocation” only when switching project/user — not on optimistic `ProjectAllocation` churn. */
  useEffect(() => {
    lastCommittedAllocationRef.current = Number(project?.ProjectAllocation ?? number.ZERO);
  }, [projectIdentityKey]);

  useEffect(() => {
    if (!setUserRole || adminUserEngagement) return;
    setUserRole(userRolesOwners.find((d) => d?.value === project?.RoleId?.toString()));
  }, [project?.RoleId, isTeamMember, setUserRole, adminUserEngagement]);

  /**
   * Filters the available time blocks by excluding blocks that are present in the workCategory.
   */
  const availableBlocks = useMemo(() => {
    if (!workCategoryIds || !availableTimeBlocksProject) return EMPTY_BLOCKS_LIST;

    const trimmedCategoriesSet = new Set((workCategoryIds || []).map((id) => id.toString().trim()));

    return availableTimeBlocksProject.filter((block) => !trimmedCategoriesSet.has(block?.Id?.toString().trim()));
  }, [availableTimeBlocksProject, workCategoryIds]);

  /**
   * Sync block-assignment state only when block fields (or project identity) change.
   * Do not reset block assignment when only Team Role or Project Allocation changes.
   * @author Bhavana
   */
  useEffect(() => {
    const allocationArray = project?.Allocation?.split(",") || [];
    const workCategoryArray = project?.WorkCategoryName?.split(",") || [];
    const workCategoryIdsArray = project?.WorkCategoryId?.split(",") || [];

    setAllocation((prev) => (syncArraysEquivalent(prev, allocationArray) ? prev : allocationArray));
    setWorkCategory((prev) => (syncArraysEquivalent(prev, workCategoryArray) ? prev : workCategoryArray));
    setWorkCategoryIds((prev) => (syncArraysEquivalent(prev, workCategoryIdsArray) ? prev : workCategoryIdsArray));

    initialValues.current = {
      allocation: [...allocationArray],
      workCategory: [...workCategoryArray],
      workCategoryIds: [...workCategoryIdsArray],
    };
  }, [projectIdentityKey, project?.Allocation, project?.WorkCategoryName, project?.WorkCategoryId]);

  /**
   * Keep top-level project allocation in sync without touching block-assignment state.
   * @author Bhavana
   */
  useEffect(() => {
    const next = project?.ProjectAllocation ?? number.ZERO;
    if (isAllocationInputFocusedRef.current || allocationDirtyRef.current) {
      return;
    }
    if (
      typeof document !== "undefined" &&
      projectAllocationPercentInputRef.current &&
      document.activeElement === projectAllocationPercentInputRef.current
    ) {
      return;
    }
    setEditedAllocation(Number(next));
  }, [projectIdentityKey, project?.ProjectAllocation]);

  /**
   * Validates the current allocation and edited allocation inputs to enable or disable
   * the save button and set an error message if validation fails.
   * @author Bhavana
   */
  useEffect(() => {
    validateInputsHelper(allocation, () => {}, setIsSaveDisabled);
  }, [allocation]);

  /**
   * calculate the total block allocation and determine if the save button should be enabled
   * @author Bhavana
   */
  useEffect(() => {
    if (allocation?.length) {
      const totalBlockAllocation = allocation.reduce((sum, val) => sum + Number(val), number.ZERO);
      if (totalBlockAllocation === number.HUNDRED && editedAllocation <= number.HUNDRED) setIsSaveDisabled(false);
    }
  }, [allocation, editedAllocation]);

  /**
   * Detect changes in block assignments using useMemo for better performance
   * @author Bhavana
   */
  const hasChanges = useMemo(() => {
    return (
      !arraysEqual(allocation, initialValues.current.allocation) ||
      !arraysEqual(workCategory, initialValues.current.workCategory) ||
      !arraysEqual(workCategoryIds, initialValues.current.workCategoryIds)
    );
  }, [allocation, workCategory, workCategoryIds]);

  /**
   * Check if the current user is the last remaining manager (RoleId === 4)
   * @author Bhavana
   */
  const isLastManager = useMemo(() => {
    if (!projectData || !project) return false;
    const managers = projectData.filter(member => parseInt(member.RoleId) === number.FOUR);
    return (managers?.length === number?.ONE || project?.managerCount === number?.ONE) && parseInt(project.RoleId) === number.FOUR;
  }, [projectData, project]);

  /**
   * Handles the remove user click with validation for last manager
   * Shows restriction popup if the last manager tries to remove themselves
   * @author Bhavana
   */
  const handleRemoveClick = () => {
    if (isLastManager && parseInt(project.UserId, 10) === parseInt(userId, 10)) {
      setShowActionRestricted(true);
      return;
    }
    handleDeleteAction(project);
  };

  /**
   * Handles change in allocation for a specific block.
   *@author Bhavana
   */
  const handleAllocationChange = useCallback((index, value) => {
    setAllocation((prev) => {
      const next = [...prev];
      next[index] = parseInt(value, 10) || 0;
      return next;
    });
  }, []);

  /**
   * Adds a new block to the allocation list.
   *@author Bhavana
   */
  const handleAddBlock = useCallback((blockName, blockId) => {
    setWorkCategory((prev) => [...prev, blockName]);
    setWorkCategoryIds((prev) => [...prev, blockId]);
    setAllocation((prev) => [...prev, ""]);
    setShowDropdown(false);
    setSearchValue("");
  }, []);

  /**
   * Removes a block from the allocation list.
   * @author Bhavana
   */
  const handleRemoveBlock = useCallback((index) => {
    setWorkCategory((prev) => prev.filter((_, idx) => idx !== index));
    setWorkCategoryIds((prev) => prev.filter((_, idx) => idx !== index));
    setAllocation((prev) => prev.filter((_, idx) => idx !== index));
  }, []);

  const toggleBlockAddDropdown = useCallback(() => {
    setShowDropdown((open) => !open);
  }, []);

  /**
   * Saves block engagement; optionally calls `onCancel` after success when `closeAfterBlockSave` is true.
   * `onSave` may be async; failures leave the sidebar open.
   * @author Bhavana
   */
  const handleSave = async () => {
    if (isSaveDisabled) return;

    const payload = {
      ...project,
      ProjectAllocation: editedAllocation,
      Allocation: allocation.join(","),
      WorkCategoryName: workCategory.join(","),
      WorkCategoryId: workCategoryIds.join(","),
      projectId: project?.ProjectId ?? ProjectId,
    };

    try {
      await Promise.resolve(onSave(payload));
      initialValues.current = {
        allocation: [...allocation],
        workCategory: [...workCategory],
        workCategoryIds: [...workCategoryIds],
      };
      if (closeAfterBlockSave) {
        onCancel?.();
      }
    } catch {
      console.error("Error saving block engagement");
    }
  };

  /**
   * Handles change in project allocation input.
   *@author Bhavana
   */
  const buildUpdatedProjectData = useCallback((source, normalizedValue) => {
    const p = projectSnapshotRef.current;
    const matchesEngagementRow = (data) =>
      isTeamMember
        ? Number(data?.UserId) === Number(p?.UserId)
        : Number(data?.ProjectId) === Number(p?.ProjectId);
    const base = Array.isArray(source) ? source : [];
    return base.map((data) =>
      matchesEngagementRow(data) ? { ...data, ProjectAllocation: normalizedValue } : data,
    );
  }, [isTeamMember]);

 /**
  * Applies project allocation.
  * @author Bhavana
  */
  const applyOptimisticProjectAllocation = useCallback(
    (normalizedValue) => {
      onProjectAllocationOptimistic?.(normalizedValue);
      let computedNext = null;
      setProjectData((prev) => {
        computedNext = buildUpdatedProjectData(prev, normalizedValue);
        return computedNext;
      });
      return computedNext ?? buildUpdatedProjectData(projectDataRef.current, normalizedValue);
    },
    [buildUpdatedProjectData, onProjectAllocationOptimistic, setProjectData],
  );

  const handleProjectAllocationChange = useCallback((e) => {
    let inputValue = e.target.value;

    if (inputValue.length > 3) {
      e.preventDefault();
      return;
    }

    if (inputValue && /^0{2,}$/.test(inputValue)) {
      inputValue = '0';
      e.target.value = '0';
    }

    // Handle leading zeros for numbers like "001" -> "1"
    if (inputValue && /^0+\d/.test(inputValue)) {
      inputValue = inputValue.replace(/^0+/, '');
      e.target.value = inputValue;
    }

    const parsed = inputValue === "" ? number.ZERO : parseInt(inputValue, 10);
    const normalizedValue = Number.isFinite(parsed) ? Math.min(number.HUNDRED, parsed) : number.ZERO;
    setEditedAllocation(normalizedValue);
    allocationDirtyRef.current = true;
  }, []);

  /**
   * Toggles edit mode for project allocation.
   * @author Bhavana
   */
  const handleSaveProjectAllocation = useCallback((inputValue) => {
    const normalizedValue =
      inputValue === "" || inputValue == null ? number.ZERO : Math.min(number.HUNDRED, parseInt(inputValue, number.TEN) || number.ZERO);
    if (Number(lastCommittedAllocationRef.current) === Number(normalizedValue)) {
      allocationDirtyRef.current = false;
      return;
    }
    const payload = {
      projectId: project?.ProjectId ?? ProjectId,
      selectedUserId: project?.UserId,
      ProjectEngagement: normalizedValue,
    };
    const updatedData = applyOptimisticProjectAllocation(normalizedValue);
    updateProjectEngagement(payload);
    lastCommittedAllocationRef.current = normalizedValue;
    allocationDirtyRef.current = false;
    
    /** Update Redux state when updating team member allocation to reflect changes in UI immediately
     * @author Muskan Thakur
     */
    if (isTeamMember && project?.UserId && Array.isArray(updatedData)) {
      const updatedMember = updatedData.find((data) => Number(data?.UserId) === Number(project?.UserId));
      if (updatedMember) {
        dispatch(updateProjectMemberAction({
          ...updatedMember,
          ProjectAllocation: normalizedValue,
        }));
      }
    }
  }, [
    ProjectId,
    applyOptimisticProjectAllocation,
    dispatch,
    isTeamMember,
    project?.ProjectId,
    project?.UserId,
  ]);

  const commitEditedAllocation = useCallback(() => {
    handleSaveProjectAllocation(editedAllocation);
  }, [editedAllocation, handleSaveProjectAllocation]);

  useEffect(() => {
    if (!allocationDirtyRef.current) return;
    if (allocationCommitTimeoutRef.current) {
      clearTimeout(allocationCommitTimeoutRef.current);
    }
    allocationCommitTimeoutRef.current = setTimeout(() => {
      if (allocationDirtyRef.current) {
        commitEditedAllocation();
      }
    }, number.THREE_HUNDRED);
    return () => {
      if (allocationCommitTimeoutRef.current) {
        clearTimeout(allocationCommitTimeoutRef.current);
      }
    };
  }, [editedAllocation, commitEditedAllocation]);

  // Function to handle key restrictions
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      commitEditedAllocation();
      return;
    }
    if (!/[0-9]/.test(e.key) && e.key !== "Backspace" && e.key !== "Tab" && e.key !== "ArrowLeft" && e.key !== "ArrowRight") {
      e.preventDefault();
    }
  };

  return (
    <div className='shadow-sm bg-white rounded z-3 position-absolute rounded-3 edit-project-details p-3' ref={sidebarRef}>
      <div className='d-flex justify-content-between mb-3'>
        <div>
          <div title={isTeamMember ? label.USER : quote.PROJECT_NAME} className='engagement-project-name text-truncate'>
            {isTeamMember ? project?.Name : project?.ProjectName}
          </div>
          <label className='text-muted' title="Job Role">
            {project?.JobRole && project?.JobRole}
          </label>
        </div>
        <div className='d-flex mt-1 gap-2'>
          {(isTeamMember || isAdminEdit) && handleDeleteAction && (
            <span
              onClick={handleRemoveClick}
              className='d-flex justify-content-center red cursor-pointer'
              title={label.REMOVE_USER_HEADER}
            >
              {icon.USER_MINUS}
            </span>
          )}
          <div onClick={onCancel} className='cursor-pointer' title={label.CLOSE}>
            {icon.ARROW_FORWARD}
          </div>
        </div>
      </div>
      {/* Action Restricted Popup for last manager */}
      {showActionRestricted && (
        <div className='action-restricted-popup shadow-sm bg-white rounded border p-3 mb-3'>
          <div className='d-flex justify-content-between align-items-start'>
            <div>
              <div className='font-weight-bold mb-2'>{label.ACTION_RESTRICTED}</div>
              <div className='text-muted small'>
                {label.ASSIGN_NEW_MANAGER_MESSAGE}
              </div>
            </div>
            <span
              className='cursor-pointer ml-2'
              onClick={() => setShowActionRestricted(false)}
              title={label.CLOSE}
            >
              {icon.CLOSE}
            </span>
          </div>
        </div>
      )}
      {checkUserValidation && (
      <div className='role-allocation-section mt-3 mb-3'>
        {/* Header Row with Labels */}
        <div className='d-flex justify-content-between mb-2'>
          <span>Team Role</span>
            <span className= "mr-4">Allocation</span>
        </div>

        {/* Input Row */}
        <div className='d-flex align-items-center'>
          <DropDownList
            className='w-50'
            id='userRoles'
            data={isAdminEdit || RoleId === number.FOUR ? userRolesOwners : userRolesCollaborators}
            value={userRole}
            popupSettings={
              adminUserEngagement && typeof document !== "undefined" ? { appendTo: document.body } : undefined
            }
            onChange={(event) =>
              onRoleChange(event, resolveRoleChangeEntityId(adminUserEngagement, project, isTeamMember, ProjectId), project?.RoleId)
            }
            textField='label'
            dataItemKey='value'
            disabled={!isAdminEdit && RoleId !== number.FOUR}
          />

          <div className='w-50 d-flex align-items-center justify-content-end gap-2'>
            <input
              ref={projectAllocationPercentInputRef}
              type='number'
              min='0'
              max='100'
              maxLength='3'
              autoComplete='off'
              value={editedAllocation}
              onChange={handleProjectAllocationChange}
              onMouseDown={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
              onFocus={() => {
                isAllocationInputFocusedRef.current = true;
              }}
              onBlur={() => {
                isAllocationInputFocusedRef.current = false;
                commitEditedAllocation();
              }}
              className='allocation-input-spinner form-control w-50 text-center'
              onKeyDown={handleKeyDown}
              aria-label='Project allocation percent'
            />
            <span>%</span>
          </div>
        </div>
      </div>)}
      {/* Engagement bar + block assignment only when top-level project allocation is greater than 0 */}
      {editedAllocation !== number.ZERO && (
        <>
          <hr className='my-2' />
          <div className='font-weight-light-semi-bold mt-3'>{label.ENGAGEMENT}</div>

          <div className='allocation-bar mt-3 d-flex'>
            {project?.Allocation && project?.WorkCategoryName
              ? project.Allocation.split(",")
                  .map((value, idx) => ({ value: value.trim(), idx }))
                  .filter(({ value }) => parseFloat(value) > 0)
                  .map(({ value, idx }, newIdx, arr) => {
                    const categoryName = project.WorkCategoryName.split(",")[idx]?.trim();
                    return (
                      <div
                        key={newIdx}
                        className={`allocation-segment segment-${newIdx % 4} ${newIdx !== arr.length - 1 ? "bar-margin" : ""}`}
                        style={{ width: `${value}%` }}
                        title={`${categoryName || ""}: ${value}%`}
                      />
                    );
                  })
              : null}
          </div>

          {workCategory.length > number.ZERO && (
            <EditSidebarBlockAssignment
              workCategory={workCategory}
              allocation={allocation}
              availableBlocks={availableBlocks}
              searchValue={searchValue}
              showDropdown={showDropdown}
              dropdownRef={dropdownRef}
              onToggleAddDropdown={toggleBlockAddDropdown}
              onSearchChange={setSearchValue}
              onAddBlock={handleAddBlock}
              onAllocationChange={handleAllocationChange}
              onRemoveBlock={handleRemoveBlock}
            />
          )}
          {workCategory.length > number.ZERO && (
            <div className='mt-3 mb-2'>
              {(() => {
                const totalBlockAllocation = allocation.reduce((sum, val) => sum + (Number(val) || 0), 0);
                const isExceeding = totalBlockAllocation > 100;
                const isLess = totalBlockAllocation < 100 && totalBlockAllocation > 0;
                if (isExceeding) {
                  return (
                    <div className='text-danger small'>
                      Total Allocation: {totalBlockAllocation}% (exceeds limit of 100%) , changes will not be saved
                    </div>
                  );
                }
                if (isLess) {
                  return (
                    <div className='text-danger small'>
                      Total Allocation: {totalBlockAllocation}% (should be 100%) , changes will not be saved
                    </div>
                  );
                }
                return null;
              })()}
            </div>
          )}
          {workCategory?.length > number.ZERO && !isSaveDisabled && hasChanges && (
            <div className='d-flex justify-content-end mt-4'>
              <button disabled={isSaveDisabled} onClick={handleSave} className='btn-width btn btn-primary'>
                {button.SAVE}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default EditProjectSidebar;
